'use client'

import { useEffect, useRef } from 'react'
import { useUserContext } from '@/context'
import { useSessionOrchestrator } from '@/hooks/useSessionOrchestrator'
import {
  closeModal,
  openModal,
} from '@/components/modals/modal.registry.events'
import {
  readLastActivity,
  writeLastActivity,
} from '@/services/auth/inactivity.client'
import { getPersistSessionPreference } from '@/services/auth/session.client'
import {
  getSessionInactivityTimeoutMs,
  SESSION_INACTIVITY_DURATION_PREFERENCE_KEY,
} from '@/services/auth/sessionPolicy'
import { captureSessionEvent } from '@/services/auth/sessionTelemetry.client'
import { pref } from '@/utils/pref'

const WARNING_LEAD_TIME = 2 * 60 * 1000
const CHECK_INTERVAL = 30 * 1000
const ACTIVITY_THROTTLE = 30 * 1000

export default function AutoLogoutListener() {
  const { user } = useUserContext()
  const { beginLogout } = useSessionOrchestrator()
  const lastWriteRef = useRef(0)
  const warningShownRef = useRef(false)
  const inactivityTimeoutMs = getSessionInactivityTimeoutMs({
    accountPreference: pref(
      user?.preferences,
      SESSION_INACTIVITY_DURATION_PREFERENCE_KEY,
    ),
    persistSession: getPersistSessionPreference(),
  })

  useEffect(() => {
    function handleStayLoggedIn() {
      const fresh = Date.now()
      writeLastActivity(fresh)
      lastWriteRef.current = fresh
      warningShownRef.current = false
    }

    function handleLogoutNow() {
      void beginLogout('inactivity')
    }

    function check() {
      const lastActivity = readLastActivity()
      if (lastActivity === null) return

      const elapsed = Date.now() - lastActivity

      if (elapsed >= inactivityTimeoutMs) {
        void beginLogout('inactivity')
        return
      }

      const warningThreshold = inactivityTimeoutMs - WARNING_LEAD_TIME

      if (elapsed >= warningThreshold && !warningShownRef.current) {
        warningShownRef.current = true
        const remainingMs = inactivityTimeoutMs - elapsed
        const remainingSeconds = Math.max(1, Math.ceil(remainingMs / 1000))

        captureSessionEvent('inactivity_warning_shown')

        openModal('inactivityWarning', {
          remainingSeconds,
          onStayLoggedIn: handleStayLoggedIn,
          onLogoutNow: handleLogoutNow,
        })
      }

      if (warningShownRef.current && elapsed < warningThreshold) {
        warningShownRef.current = false
        closeModal()
        captureSessionEvent('inactivity_warning_dismissed')
      }
    }

    const now = Date.now()
    const initialLastActivity = readLastActivity()

    if (initialLastActivity === null) {
      writeLastActivity(now)
      lastWriteRef.current = now
    } else {
      lastWriteRef.current = initialLastActivity
    }

    check()

    function handleActivity() {
      const current = Date.now()
      if (current - lastWriteRef.current >= ACTIVITY_THROTTLE) {
        writeLastActivity(current)
        lastWriteRef.current = current
      }
    }

    let intervalId: ReturnType<typeof setInterval> | null = setInterval(
      check,
      CHECK_INTERVAL,
    )

    function handleVisibility() {
      if (document.hidden) {
        if (intervalId !== null) {
          clearInterval(intervalId)
          intervalId = null
        }
      } else {
        check()
        if (intervalId === null) {
          intervalId = setInterval(check, CHECK_INTERVAL)
        }
      }
    }

    window.addEventListener('pointerdown', handleActivity)
    window.addEventListener('keydown', handleActivity)
    window.addEventListener('scroll', handleActivity, { passive: true })
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      if (intervalId !== null) clearInterval(intervalId)
      window.removeEventListener('pointerdown', handleActivity)
      window.removeEventListener('keydown', handleActivity)
      window.removeEventListener('scroll', handleActivity)
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [beginLogout, inactivityTimeoutMs])

  return null
}
